/**

 * BANK-IBAN-R3.5 — customer Mini App mirror of Worker canonical display state.

 * Keep in sync with workers-api/src/lib/commerce-lifecycle/canonical-display-state.ts

 */

import {

  isAwaitingManagerAvailability,

  normalizePaymentTiming,

  type CustomerPaymentTiming,

} from "./customer-payment-timing-v2.js";



export const CANONICAL_DISPLAY_STATES = [

  "CANCELLED",

  "REFUND_PENDING",

  "REFUNDED",

  "RECEIVED",

  "READY_FOR_PICKUP",

  "IN_TRANSIT",

  "AWAITING_MANAGER_ACCEPTANCE",

  "PAID",

  "PAYMENT_REVIEW",

  "AWAITING_STOCK_CONFIRMATION",

  "AWAITING_BANK_CONFIRMATION",

  "AWAITING_PAYMENT",

  "NEW",

] as const;



export type CanonicalDisplayState = (typeof CANONICAL_DISPLAY_STATES)[number];



function norm(v: string | null | undefined): string {

  return String(v ?? "")

    .trim()

    .toLowerCase();

}



function isBankIbanMethod(method: string | null | undefined): boolean {

  const m = norm(method);

  return m.includes("bank_transfer") || m.includes("fop") || m === "iban" || m === "iban_transfer";

}



function isPaid(status: string | null | undefined, bankConfirmedAt?: string | null, bankConfirmed?: boolean | null): boolean {

  if (bankConfirmed === true) return true;

  if (String(bankConfirmedAt ?? "").trim()) return true;

  const p = norm(status);

  return p === "paid" || p.startsWith("paid") || p === "payment_confirmed_manual" || p === "payment_confirmed_bank_iban" || p === "manually_paid";

}



export function projectCanonicalDisplayState(input: {

  order_top_status?: string | null;

  payment_status?: string | null;

  payment_method?: string | null;

  ttn_status?: string | null;

  delivery_status?: string | null;

  status_rail?: string | null;

  proof_submitted?: boolean | null;

  display_state?: string | null;

  payment_timing?: CustomerPaymentTiming | string | null;

  note?: string | null;

  /** HR01 — bank confirmation outranks stale payment_in_review. */
  bank_confirmed_at?: string | null;

  bank_confirmed?: boolean | null;

}): CanonicalDisplayState {

  const top = norm(input.order_top_status);

  const pay = norm(input.payment_status);

  const ttn = norm(input.ttn_status ?? input.delivery_status);

  if (pay === "refund_pending" || pay === "payment_refund_needed") return "REFUND_PENDING";

  if (isPaid(pay, input.bank_confirmed_at, input.bank_confirmed)) {

    if (top === "canceled" || top === "cancelled") return "CANCELLED";

    if (top === "completed" || ttn.includes("delivered") || ttn.includes("received")) return "RECEIVED";

    if (top === "new" || top === "") return "AWAITING_MANAGER_ACCEPTANCE";

    return "PAID";

  }

  const fromApi = String(input.display_state ?? "").trim().toUpperCase();

  if ((CANONICAL_DISPLAY_STATES as readonly string[]).includes(fromApi)) {

    if (fromApi === "CANCELLED" && (pay === "refund_pending" || pay === "payment_refund_needed")) {

      return "REFUND_PENDING";

    }

    return fromApi as CanonicalDisplayState;

  }

  if (top === "canceled" || top === "cancelled") return "CANCELLED";

  if (pay === "refunded") return "REFUNDED";

  if (top === "completed" || ttn.includes("delivered") || ttn.includes("received")) return "RECEIVED";

  if (ttn.includes("ready_for_pickup") || ttn.includes("arrived")) return "READY_FOR_PICKUP";

  if (ttn.includes("transit") || ttn.includes("handed") || ttn.includes("ship")) return "IN_TRANSIT";

  if (input.proof_submitted || pay === "payment_in_review" || pay === "receipt_review") {

    return "PAYMENT_REVIEW";

  }

  const timing = normalizePaymentTiming(

    input.payment_timing ??

      (String(input.note ?? "").match(/__payment_timing__:([a-z_]+)/i)?.[1] ?? null),

  );

  if (

    !isPaid(pay, input.bank_confirmed_at, input.bank_confirmed) &&

    isBankIbanMethod(input.payment_method) &&

    isAwaitingManagerAvailability(timing, input.payment_status, input.order_top_status)

  ) {

    return "AWAITING_STOCK_CONFIRMATION";

  }

  if (!isPaid(pay, input.bank_confirmed_at, input.bank_confirmed) && isBankIbanMethod(input.payment_method)) return "AWAITING_BANK_CONFIRMATION";

  if (

    pay.includes("pending") ||

    pay.includes("await") ||

    pay === "unpaid" ||

    pay === "" ||

    pay.includes("expired") ||

    pay.includes("failed")

  ) {

    return "AWAITING_PAYMENT";

  }

  return "NEW";

}



export function canonicalDisplayLabelUk(state: CanonicalDisplayState): string {

  switch (state) {

    case "CANCELLED":

      return "Скасовано";

    case "REFUND_PENDING":

      return "Запит на скасування / повернення";

    case "REFUNDED":

      return "Повернення коштів";

    case "RECEIVED":

      return "Отримано";

    case "READY_FOR_PICKUP":

      return "Готове до отримання";

    case "IN_TRANSIT":

      return "Відправлено";

    case "AWAITING_MANAGER_ACCEPTANCE":

      return "Очікуємо підтвердження магазину";

    case "PAID":

      return "Оплачено";

    case "PAYMENT_REVIEW":

      return "Перевіряємо оплату";

    case "AWAITING_STOCK_CONFIRMATION":

      return "Очікуємо підтвердження наявності";

    case "AWAITING_BANK_CONFIRMATION":

      return "Очікуємо підтвердження банку";

    case "AWAITING_PAYMENT":

      return "Очікуємо оплату";

    case "NEW":

    default:

      return "Нове";

  }

}



export function canonicalDisplayChipUk(state: CanonicalDisplayState): string {

  if (state === "CANCELLED") return "Скасовано";

  if (state === "AWAITING_STOCK_CONFIRMATION") return "Очікуємо підтвердження магазину";

  if (state === "AWAITING_BANK_CONFIRMATION") return "Очікуємо оплату";

  if (state === "PAYMENT_REVIEW") return "Перевіряємо оплату";

  if (state === "PAID") return "Оплачено";

  if (state === "AWAITING_MANAGER_ACCEPTANCE") return "Очікуємо підтвердження магазину";

  if (state === "AWAITING_PAYMENT") return "Очікуємо оплату";

  if (state === "NEW") return "Нове";

  if (state === "IN_TRANSIT") return "Відправлено";

  if (state === "READY_FOR_PICKUP") return "Готове до отримання";

  if (state === "RECEIVED") return "Отримано";

  if (state === "REFUND_PENDING") return "Запит на скасування / повернення";

  if (state === "REFUNDED") return "Повернення коштів";

  return canonicalDisplayLabelUk(state);

}

/** CUSTOMER-R4.10 — after availability confirm, prefer unlocked payment chip. */
export function canonicalDisplayChipUkForOrder(
  state: CanonicalDisplayState,
  paymentTiming?: string | null,
): string {
  const timing = String(paymentTiming ?? "").toLowerCase();
  const afterManager =
    timing === "after_manager_confirmation" || timing === "confirm_availability_first";
  if (
    afterManager &&
    (state === "AWAITING_PAYMENT" || state === "AWAITING_BANK_CONFIRMATION")
  ) {
    return "Наявність підтверджено · очікуємо оплату";
  }
  return canonicalDisplayChipUk(state);
}



/** Compatible list subline — never mixes cancelled with awaiting-bank copy. */

export function canonicalListSubtitleUk(state: CanonicalDisplayState): string | null {

  if (state === "CANCELLED") return "Замовлення скасовано";

  if (state === "REFUND_PENDING") return "Запит на скасування / повернення";

  if (state === "REFUNDED") return "Кошти повернуто";

  if (state === "PAID") return null;

  if (state === "AWAITING_MANAGER_ACCEPTANCE") return "Очікуємо підтвердження магазину";

  if (state === "AWAITING_STOCK_CONFIRMATION") return "Очікуємо підтвердження наявності";

  if (state === "AWAITING_BANK_CONFIRMATION") return "Очікуємо підтвердження банку";

  if (state === "AWAITING_PAYMENT") return "Очікуємо оплату";

  if (state === "PAYMENT_REVIEW") return "Перевіряємо оплату";

  return null;

}



export function hideCustomerReceiptLine(state: CanonicalDisplayState, receiptState?: string | null): boolean {

  if (state === "CANCELLED" || state === "REFUND_PENDING" || state === "REFUNDED") {

    const rs = String(receiptState ?? "").toLowerCase();

    return rs !== "ready" && rs !== "fiscalized";

  }

  return false;

}



export function hideCustomerPaymentCta(state: CanonicalDisplayState): boolean {

  return (

    state === "CANCELLED" ||

    state === "PAID" ||

    state === "AWAITING_MANAGER_ACCEPTANCE" ||

    state === "RECEIVED" ||

    state === "READY_FOR_PICKUP" ||

    state === "IN_TRANSIT" ||

    state === "REFUND_PENDING" ||

    state === "REFUNDED" ||

    state === "AWAITING_STOCK_CONFIRMATION"

  );

}

